import { QuestionType } from '@features/event-registration/types';
import { isBasicField } from '@features/forms/components/wizard-steps/fields-step/helpers';
import { FormFieldData } from '@features/forms/types';
import { FormUpdateFieldApi } from '@features/forms/types/formCreateApi';

import { normalizeFormFieldId } from './mapFormApiFields';
import { mapFormFieldToCreateApi } from './mapWizardToCreateFormRequest';
import { getFormFieldGlobalDisplayOrder } from './sortFormFieldsForApiOrder';

export type FormFieldUpdateEntry = FormFieldData & { isDeleted?: boolean };

type FieldMatch = {
  previous: FormFieldData;
  current: FormFieldData;
};

const isPersistedFieldId = (id: string | number) =>
  /^\d+$/.test(normalizeFormFieldId(id));

const areOptionsChanged = (
  current: string[] | undefined,
  previous: string[] | undefined
) => {
  const options = current ?? [];
  const prevOptions = previous ?? [];
  if (options.length !== prevOptions.length) {
    return true;
  }
  return !options.every((option) => prevOptions.includes(option));
};

const isFormFieldMetadataChanged = (
  current: FormFieldData,
  previous: FormFieldData
): boolean => {
  if (current.type !== previous.type) {
    return true;
  }

  switch (current.type) {
    case QuestionType.TEXT:
    case QuestionType.TEXT_AREA:
    case QuestionType.NUMBER:
    case QuestionType.DATE:
    case QuestionType.MOBILE_NUMBER:
      return false;
    case QuestionType.DROP_DOWN:
    case QuestionType.MULTIPLE_CHOICE:
    case QuestionType.SINGLE_CHOICE:
      return areOptionsChanged(current.options, previous.options);
    case QuestionType.FILE_UPLOAD:
      return (
        current.maxValue !== previous.maxValue ||
        areOptionsChanged(current.options, previous.options)
      );
    default:
      return false;
  }
};

const isFieldOrderChanged = (
  current: FormFieldData,
  previous: FormFieldData,
  currentFields: FormFieldData[],
  previousFields: FormFieldData[]
) =>
  getFormFieldGlobalDisplayOrder(current, currentFields, previousFields) !==
  getFormFieldGlobalDisplayOrder(previous, previousFields, currentFields);

const sortByFieldOrder = (fields: FormFieldUpdateEntry[]) =>
  [...fields].sort((a, b) => a.order - b.order);

const normalizeFieldLabel = (label: string) =>
  label.trim().toLowerCase().replace(/\s+/g, ' ');

const buildFieldMatches = (
  previousFields: FormFieldData[],
  currentFields: FormFieldData[]
): FieldMatch[] => {
  const matches: FieldMatch[] = [];
  const consumedCurrentIds = new Set<string>();
  const consumedPreviousIds = new Set<string>();

  previousFields.forEach((previous) => {
    const previousId = normalizeFormFieldId(previous.id);
    if (consumedPreviousIds.has(previousId)) {
      return;
    }

    const current = currentFields.find(
      (field) =>
        !consumedCurrentIds.has(normalizeFormFieldId(field.id)) &&
        normalizeFormFieldId(field.id) === previousId
    );

    if (!current) {
      return;
    }

    matches.push({ previous, current });
    consumedCurrentIds.add(normalizeFormFieldId(current.id));
    consumedPreviousIds.add(previousId);
  });

  previousFields.forEach((previous) => {
    const previousId = normalizeFormFieldId(previous.id);
    if (consumedPreviousIds.has(previousId) || !isBasicField(previous)) {
      return;
    }

    const normalizedLabel = normalizeFieldLabel(previous.label);
    const current = currentFields.find(
      (field) =>
        !consumedCurrentIds.has(normalizeFormFieldId(field.id)) &&
        isBasicField(field) &&
        normalizeFieldLabel(field.label) === normalizedLabel
    );

    if (!current) {
      return;
    }

    matches.push({ previous, current });
    consumedCurrentIds.add(normalizeFormFieldId(current.id));
    consumedPreviousIds.add(previousId);
  });

  return matches;
};

const findMatchForCurrentField = (
  field: FormFieldData,
  matches: FieldMatch[]
): FieldMatch | undefined =>
  matches.find(
    (match) =>
      normalizeFormFieldId(match.current.id) === normalizeFormFieldId(field.id)
  );

const isFieldIncludedInUpdates = (
  previous: FormFieldData,
  current: FormFieldData,
  updatedFields: FormFieldUpdateEntry[]
): boolean =>
  updatedFields.some((field) => {
    const fieldId = normalizeFormFieldId(field.id);
    const previousId = normalizeFormFieldId(previous.id);
    const currentId = normalizeFormFieldId(current.id);

    return (
      fieldId === previousId ||
      fieldId === currentId ||
      (!field.isDeleted &&
        isBasicField(current) &&
        normalizeFieldLabel(field.label) === normalizeFieldLabel(current.label))
    );
  });

const appendUnchangedBasicFields = (
  updatedFields: FormFieldUpdateEntry[],
  matches: FieldMatch[]
): FormFieldUpdateEntry[] => {
  const unchangedBasic = matches
    .filter(
      ({ previous, current }) =>
        isBasicField(current) &&
        !isFieldIncludedInUpdates(previous, current, updatedFields)
    )
    .map(({ previous, current }) => ({
      ...current,
      id: isPersistedFieldId(previous.id) ? previous.id : current.id
    }));

  return sortByFieldOrder([...unchangedBasic, ...updatedFields]);
};

export const getUpdatedFormFields = (
  previousFields: FormFieldData[],
  currentFields: FormFieldData[]
): FormFieldUpdateEntry[] => {
  const matches = buildFieldMatches(previousFields, currentFields);
  const matchedPreviousIds = new Set(
    matches.map((match) => normalizeFormFieldId(match.previous.id))
  );

  const deletedFields = previousFields
    .filter(
      (field) =>
        isPersistedFieldId(field.id) &&
        !matchedPreviousIds.has(normalizeFormFieldId(field.id))
    )
    .map((field) => ({ ...field, isDeleted: true }));

  const updatedFields: FormFieldUpdateEntry[] = [];

  currentFields.forEach((field) => {
    const match = findMatchForCurrentField(field, matches);

    if (!match) {
      updatedFields.push({ ...field, id: '' });
      return;
    }

    const { previous: previousField } = match;

    if (!isPersistedFieldId(previousField.id)) {
      const isLabelChanged = previousField.label !== field.label;
      const isMetadataChanged = isFormFieldMetadataChanged(
        field,
        previousField
      );
      const isOrderChanged = isFieldOrderChanged(
        field,
        previousField,
        currentFields,
        previousFields
      );
      const isRequiredChanged = field.required !== previousField.required;

      if (
        isLabelChanged ||
        isMetadataChanged ||
        isOrderChanged ||
        isRequiredChanged
      ) {
        updatedFields.push(field);
      }
      return;
    }

    const isLabelChanged = previousField.label !== field.label;
    const isMetadataChanged = isFormFieldMetadataChanged(field, previousField);
    const isOrderChanged = isFieldOrderChanged(
      field,
      previousField,
      currentFields,
      previousFields
    );
    const isRequiredChanged = field.required !== previousField.required;

    if (isLabelChanged || isMetadataChanged) {
      updatedFields.push({ ...field, id: '', isDeleted: false });
      updatedFields.push({ ...previousField, isDeleted: true });
      return;
    }

    if (isOrderChanged || isRequiredChanged) {
      updatedFields.push({
        ...field,
        id: isPersistedFieldId(previousField.id) ? previousField.id : field.id
      });
    }
  });

  return appendUnchangedBasicFields(
    [...updatedFields, ...deletedFields],
    matches
  );
};

export const mapFormFieldToUpdateApi = (
  field: FormFieldUpdateEntry,
  displayOrder?: number
): FormUpdateFieldApi => {
  const apiField: FormUpdateFieldApi = {
    ...mapFormFieldToCreateApi(field, displayOrder),
    to_be_deleted: Boolean(field.isDeleted)
  };

  if (isPersistedFieldId(field.id)) {
    apiField.id = Number(normalizeFormFieldId(field.id));
  }

  return apiField;
};
